import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../components/Loading';
import { Cell, buildCells, freeStar, mobImgUrl } from '../utils/monsterCells';
import { monthDayTime } from '../utils/date';
import ProfileWithName from '../components/ProfileWithName';
import Caret from '../components/Caret';
import DefenseTrend from '../components/DefenseTrend';
import BackButton from '../components/BackButton';
import { loadMonsterMapping } from '../utils/monsterMapping';
import { useBackHandler } from '../hooks/useBackHandler';
import { guildImg } from '../utils/guildImage';

/**
 * 방덱 — 어떤 방덱을 짜고 **누가 그 덱을 세우나**.
 *
 * 점령전은 같은 방덱을 여러 자리에 세우므로 덱 하나에 **여러 사람이 배치**된다. 그래서
 * 화면을 세로로 반 갈라 **왼쪽은 방덱 목록, 오른쪽은 고른 덱의 배치**를 둔다 — 덱을 누르면
 * 그 덱에 배치된 사람이 오른쪽에 나온다.
 *
 * 배치의 조건은 하나다: **그 몹을 가진 사람만.** 판단은 보유몹 탭에서 세어 둔 개수로 하고,
 * 같은 몹을 여러 마리 가진 사람은 그만큼 여러 덱을 맡을 수 있다 — 한 마리뿐인 몹으로 두
 * 덱을 세울 수는 없으니 **이미 맡은 덱들이 쓰는 만큼을 빼고** 남은 마리 수로 따진다.
 * 태생 3성 이하는 누구나 가진 것으로 본다. 같은 계산을 서버도 한다
 * (`routes/defenseDeck.ts`) — 화면 계산은 두 사람이 동시에 배치하면 낡은 값이기 때문이다.
 *
 * 폰에서는 반으로 가를 폭이 없어서 위아래로 쌓는다(목록 먼저, 그 아래 배치).
 */

interface Member {
    uuid: string;
    nickname: string;
    profileImg: string | null;
}

/**
 * 그 조합의 전적.
 *
 * 점령전 기록에서 이 조합이 **방어로 나온** 싸움을 모은 값이다(`rate` = 방성률, 막아낸 비율).
 * 우리 길드가 방어한 기록은 아직 수집하지 않으므로, 이 값은 "이 조합이 방어로 섰을 때 얼마나
 * 막았나" — 통계 탭이 보여주는 값과 같은 셈이다. 기록이 없으면 서버가 `null` 을 주고, 그때는
 * 아무것도 그리지 않는다.
 */
interface DeckStats {
    total: number;
    held: number;
    broken: number;
    rate: number;
}

interface Deck {
    id: string;
    /** `가문|속성` 세 개. 첫 번째가 리더 */
    keys: string[];
    /** 이 덱에 배치된 사람의 uuid */
    members: string[];
    order: number;
    stats?: DeckStats | null;
    /**
     * **그 덱을 세운 사람들 각자의 전적** (uuid → 전적).
     *
     * `stats` 는 길드 전체의 합이라 "이 사람 자리가 얼마나 막았나" 와 다르다 — 같은 덱이라도
     * 룬과 순서가 달라 사람마다 다르게 막힌다. 서버가 방어 기록의 `wizardId` 로 갈라 준다.
     */
    memberStats?: Record<string, DeckStats>;
}

const DeckPlan: React.FC<{ isMobile: boolean; user?: any }> = ({ isMobile }) => {
    const [mapping, setMapping] = useState<any>(null);
    const [decks, setDecks] = useState<Deck[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    /** 덱에 쓰인 열쇠만 담긴 보유 개수 `{ uuid: { 열쇠: 마리수 } }` */
    const [counts, setCounts] = useState<Record<string, Record<string, number>>>({});
    const [myUuid, setMyUuid] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [problem, setProblem] = useState<string | null>(null);

    /** 오른쪽에 배치를 보여줄 덱 */
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
          loadMonsterMapping()
            .then(setMapping)
            .catch(() => {});
    }, []);

    /** 게임에서 배치를 마지막으로 받아온 시각 */
    const [syncedAt, setSyncedAt] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await fetch('/api/defense-decks', { credentials: 'include' });
            if (!r.ok) {
                setProblem(`방덱을 못 받았습니다 (HTTP ${r.status})`);
            } else {
                const d = await r.json();
                setDecks(d.decks || []);
                setMembers(d.members || []);
                setCounts(d.counts || {});
                setMyUuid(d.myUuid || null);
                setSyncedAt(d.syncedAt ?? null);
            }
        } catch (e: any) {
            setProblem(`방덱을 못 받았습니다 (${e?.message || '연결 실패'})`);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    /**
     * 열쇠 → 칸(이름·그림·태생 성급). 보유몹과 같은 규칙으로 만든다.
     *
     * 보유몹은 태생 4~5성만 다루지만 **방덱에는 3성 이하도 넣을 수 있어서**(모두 가진
     * 것으로 본다) 성급 전부를 담는다 — 안 담으면 그 몹의 이름·그림을 못 찾는다.
     */
    const cellByKey = useMemo(() => {
        const m = new Map<string, Cell>();
        for (const c of buildCells(mapping, [1, 2, 3, 4, 5])) if (!m.has(c.key)) m.set(c.key, c);
        return m;
    }, [mapping]);

    const labelOf = useCallback(
        (key: string) => cellByKey.get(key)?.label || key.split('|')[0],
        [cellByKey]
    );

    /**
     * 한 사람이 **이미 쓰고 있는** 마리 수.
     *
     * 같은 덱을 **여러 자리에 세울 수 있다**(점령전은 그렇게 한다). `members` 에 같은 사람이
     * 두 번 들어 있으면 그만큼 두 번 센다 — 자리 수만큼 몹이 더 필요하다.
     */
    const usedBy = useCallback((uuid: string) => {
        const out: Record<string, number> = {};
        for (const d of decks) {
            const times = d.members.filter((u) => u === uuid).length;
            if (!times) continue;
            for (const k of d.keys) out[k] = (out[k] || 0) + times;
        }
        return out;
    }, [decks]);

    /**
     * 이 사람이 이 덱을 **한 자리 더** 세울 수 있나 — 몹 하나하나의 사정까지 준다.
     *
     * 화면이 덱의 세 몹을 모두 보여주고 밝기로 보유를 나타내므로, 열쇠마다
     * "가졌나"(`owned`)와 "가졌지만 이미 다 쓰고 있나"(`usedUp`)가 필요하다. 이 둘은 사람이
     * 해야 할 일이 다르다 — 하나는 몹을 키우거나 보유몹을 고치는 것이고, 하나는 배치를
     * 옮기는 것이다.
     *
     * **이 덱에서 이미 차지한 자리도 센다** — 한 자리를 더 세우려면 몹이 그만큼 더 있어야 한다.
     */
    const fieldable = useCallback((uuid: string, keys: string[]) => {
        const have = counts[uuid] || {};
        const used = usedBy(uuid);
        const states = keys.map((k) => {
            // 태생 3성 이하는 누구나 가진 것으로 본다 (보유몹에서 세지도 않는다)
            const free = freeStar(cellByKey.get(k)?.natural);
            const n = have[k] || 0;
            return {
                k,
                owned: free || n > 0,
                usedUp: !free && n > 0 && n < (used[k] || 0) + 1
            };
        });
        return { ok: states.every((s) => s.owned && !s.usedUp), states };
    }, [counts, usedBy, cellByKey]);

    const nameOf = useMemo(() => new Map(members.map((m) => [m.uuid, m])), [members]);

    /**
     * 덱에 배치된 사람을 **자리 수와 함께**, 많이 세운 사람부터.
     *
     * 같은 사람이 같은 덱을 여러 자리에 세울 수 있어서 `members` 에 같은 uuid 가 여러 번
     * 들어 있다. 이름표를 그만큼 여러 개 그리면 지저분하니 하나로 묶고 자리 수를 적는다.
     * 많이 세운 사람이 앞에 오는 것이 이 덱을 누가 주로 맡았나를 먼저 보여 준다.
     */
    const seatsOf = useCallback((deck: Deck) => {
        const out: { uuid: string; n: number }[] = [];
        for (const u of deck.members) {
            const hit = out.find((x) => x.uuid === u);
            if (hit) hit.n += 1;
            else out.push({ uuid: u, n: 1 });
        }
        return out.sort((a, b) => b.n - a.n
            || (nameOf.get(a.uuid)?.nickname || '').localeCompare(nameOf.get(b.uuid)?.nickname || ''));
    }, [nameOf]);

    /**
     * 방덱의 등급 — **태생 5성이 하나라도 있으면 5성 방덱**, 하나도 없으면 4성 방덱.
     *
     * 길드에서 부르는 방식이다(5성을 넣은 덱은 그만큼 귀한 자원을 쓴 덱이다).
     */
    const gradeOf = useCallback(
        (d: Deck) => (d.keys.some((k) => cellByKey.get(k)?.natural === 5) ? 5 : 4),
        [cellByKey]
    );

    /** 지금 보고 있는 등급 — 5성부터 본다(점령전에서 먼저 보는 쪽이다) */
    const [grade, setGrade] = useState<5 | 4>(5);

    /**
     * **무엇을 세로로 늘어놓을까.**
     *
     *   deck    방덱이 줄이고, 그 덱을 세운 사람들이 옆에 붙는다 (원래 화면)
     *   member  길드원이 줄이고, 그 사람이 세운 방덱들이 옆에 붙는다
     *
     * 같은 자료를 뒤집어 보는 것뿐이다. "이 덱 누가 세웠나" 와 "쟤는 뭘 세웠나" 는 점령전
     * 준비에서 둘 다 자주 묻는 물음인데, 뒤엣것은 덱을 하나씩 열어 보며 이름을 찾아야 했다.
     */
    const [viewMode, setViewMode] = useState<'deck' | 'member'>('deck');

    /**
     * **펼쳐 본 길드원.** 한 사람씩만 편다(전투기록 탭의 길드원 목록과 같다).
     *
     * 스물아홉 명이 저마다 덱을 대여섯 개씩 달고 있으면 화면이 수백 줄이 된다. 누구를 볼지
     * 먼저 고르고 그 사람만 펴는 편이 찾기 쉽다.
     */
    const [openMember, setOpenMember] = useState<string | null>(null);

    /**
     * **누구의 기록만 볼까.** 길드원 목록에서 덱을 눌러 들어오면 그 사람으로 맞춘다.
     *
     * 같은 방덱을 여럿이 세우므로 덱의 전적은 길드 전체의 합이다. 그런데 길드원 목록에서
     * 누르고 들어왔다면 궁금한 것은 **그 사람 자리의 성적**이다 — 같은 덱이라도 룬과 순서가
     * 달라 사람마다 다르게 막힌다.
     */
    const [focusMember, setFocusMember] = useState<string | null>(null);

    /**
     * 보여줄 방덱 — 고른 등급만, **많이 세운 덱이 위로**.
     *
     * 손으로 정하던 순서는 없앴다: 많이 세운 덱이 곧 우리 길드의 주력이라 그게 위에 있어야 한다.
     */
    const visibleDecks = useMemo(
        () => decks.filter((d) => gradeOf(d) === grade).sort((a, b) => b.members.length - a.members.length),
        [decks, grade, gradeOf]
    );
    /**
     * **길드원별 묶음** — 사람마다 (덱, 자리 수).
     *
     * 지금 보고 있는 등급만 센다(위 목록과 같은 기준이라야 숫자가 서로 안 어긋난다).
     * 많이 세운 사람이 위로 — 점령전에서 자리를 많이 채운 사람이 먼저 눈에 들어와야 한다.
     * 한 자리도 안 세운 사람은 아래에 따로 적는다(누가 아직 안 세웠나가 이 화면의 쓸모다).
     */
    const byMember = useMemo(() => {
        const seats = new Map<string, { deck: Deck; n: number }[]>();
        for (const d of visibleDecks) {
            for (const uuid of d.members) {
                const list = seats.get(uuid) || [];
                const hit = list.find((x) => x.deck.id === d.id);
                if (hit) hit.n += 1;
                else list.push({ deck: d, n: 1 });
                seats.set(uuid, list);
            }
        }
        const placed = members
            .filter((m) => seats.has(m.uuid))
            .map((m) => {
                const mine = (seats.get(m.uuid) || []).sort((a, b) => b.n - a.n);
                /**
                 * **그 사람이 세운 방덱들의 전적.**
                 *
                 * 덱의 전체 전적(`stats`)이 아니라 **그 사람 자리의 전적**(`memberStats`)을 합친다 —
                 * 같은 덱을 여럿이 세우므로 전체 합은 남의 성적까지 섞인 값이다.
                 *
                 * 평균 방성률은 덱별 비율의 평균이 아니라 **합쳐서 다시 나눈 값**이다. 한 번
                 * 싸운 덱과 삼백 번 싸운 덱을 같은 무게로 평균 내면 숫자가 거짓말을 한다.
                 */
                let total = 0;
                let held = 0;
                for (const { deck } of mine) {
                    const own = deck.memberStats?.[m.uuid];
                    if (!own) continue;
                    total += own.total;
                    held += own.held;
                }
                return {
                    member: m,
                    decks: mine,
                    seats: mine.reduce((sum, x) => sum + x.n, 0),
                    record: total > 0 ? { total, held, rate: Math.round((held / total) * 1000) / 10 } : null
                };
            })
            .sort((a, b) => b.decks.length - a.decks.length
                || a.member.nickname.localeCompare(b.member.nickname));
        return { placed };
    }, [visibleDecks, members]);

    /**
     * 오른쪽에 보여줄 덱.
     *
     * 고른 덱이 지금 보고 있는 등급에 없으면(등급을 바꿨거나 덱이 지워졌다) 맨 위 덱으로 간다.
     */
    /**
     * **폰에서는 목록과 배치를 오간다** (통계 탭과 같은 방식).
     *
     * 예전에는 배치를 목록 **아래**에 붙였다. 폰에서는 덱을 누르면 화면 밖 아래에서 값이
     * 바뀌어서, 눌러도 아무 일도 안 일어난 것처럼 보였다. 그래서 누르면 목록을 접고 배치만
     * 띄운다 — 돌아가는 길은 [← 뒤로] 다.
     *
     * `selected` 가 아니라 `selectedId` 로 판단한다. `selected` 는 아무것도 안 골랐을 때
     * 첫 덱으로 떨어지므로, 그걸 쓰면 폰에서 목록이 처음부터 안 보인다.
     */
    const detailOpen = isMobile && !!selectedId;

    const selected = useMemo(
        () => visibleDecks.find((d) => d.id === selectedId) || visibleDecks[0] || null,
        [visibleDecks, selectedId]
    );

    /**
     * **이 방덱으로 방어한 기록.**
     *
     * "누가 세울 수 있나" 다음에 따라오는 물음이 "그래서 이 덱이 막아 주나" 다. 통계 탭이
     * 쓰는 길(`/api/posts/search-counter`, side=own)을 그대로 쓴다 — 우리 방덱이면 카운터
     * 대신 **방어 기록**이 온다.
     *
     * 덱마다 서버가 기록 전체를 훑어서 1~2초 걸린다(실측). 그래서 한 번 받은 덱은 들고
     * 있다가 다시 고르면 그대로 그린다 — 목록과 배치를 오갈 때마다 다시 받을 일이 아니다.
     */
    const [records, setRecords] = useState<any[] | null>(null);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const recordCache = useRef(new Map<string, any[]>());

    useEffect(() => {
        const deck = selected;
        if (!deck) { setRecords(null); return; }

        const cached = recordCache.current.get(deck.id);
        if (cached) { setRecords(cached); setRecordsLoading(false); return; }

        let alive = true;
        setRecordsLoading(true);
        setRecords(null);
        (async () => {
            try {
                const res = await fetch('/api/posts/search-counter', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        side: 'own',
                        // 열쇠가 `가문|속성` 이라 그대로 갈라 보내면 된다
                        monsters: deck.keys.map((k) => ({ name: k.split('|')[0], attribute: k.split('|')[1] }))
                    })
                });
                const data = await res.json();
                const rows = Array.isArray(data?.records) ? data.records : [];
                recordCache.current.set(deck.id, rows);
                if (alive) setRecords(rows);
            } catch {
                if (alive) setRecords([]);
            } finally {
                if (alive) setRecordsLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [selected]);

    /**
     * 화면에 그릴 기록. 한 사람으로 좁혀 놓았으면 그 사람이 방어한 것만 남긴다.
     * 기록에는 방어한 길드원의 uuid 가 실려 온다(`authorUuid`).
     */
    const shownRecords = useMemo(() => {
        if (!records) return null;
        if (!focusMember) return records;
        return records.filter((r) => String(r.authorUuid || '') === focusMember);
    }, [records, focusMember]);

    /** 막아낸 비율 — 기록이 있을 때만 센다 */
    const recordStat = useMemo(() => {
        if (!shownRecords || shownRecords.length === 0) return null;
        const win = shownRecords.filter((r) => r.result === 'win').length;
        return { total: shownRecords.length, win, rate: Math.round((win / shownRecords.length) * 1000) / 10 };
    }, [shownRecords]);



    // 뒤로 가기: 폰에서 연 배치 화면 → 덱 목록
    useBackHandler(detailOpen, () => setSelectedId(null));

    const tile = isMobile ? 44 : 52;

    return (
        <div style={{
            width: '100%',
            minHeight: 'calc(100vh - 60px)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            paddingBottom: 'clamp(32px, 6vw, 40px)'
        }}>
            <div style={{
                maxWidth: 1200,
                margin: '0 auto',
                padding: 'clamp(16px, 3vw, 20px) clamp(12px, 2.5vw, 16px)'
            }}>
                {/* 머리말 */}
                <div style={{ ...card(), marginBottom: 'clamp(12px, 2.5vw, 16px)', textAlign: 'center' }}>
                    <h1 style={{
                        margin: 0,
                        marginBottom: 6,
                        fontSize: isMobile ? 'clamp(22px, 5vw, 26px)' : 34,
                        fontWeight: 900,
                        letterSpacing: '-0.5px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>방덱</h1>
                    <p style={{ margin: 0, color: '#999', fontSize: isMobile ? 'clamp(10px, 2vw, 12px)' : 13, fontWeight: 500 }}>
                        1시간마다 자동으로 인게임 내 방덱 현황이 업데이트됩니다.
                    </p>
                    {/*
                      * 언제 것인지 적어 준다 — 수집이 멈춰도 화면은 멀쩡해 보이기 때문이다.
                      * 배치가 안 바뀌어도 받아온 시각은 갱신된다(서버가 따로 남긴다).
                      */}
                    {syncedAt && (
                        <p style={{
                            margin: '4px 0 0',
                            color: '#868e96',
                            fontSize: isMobile ? 'clamp(10px, 2vw, 12px)' : 12,
                            fontWeight: 600
                        }}>
                            마지막 갱신 {monthDayTime(syncedAt, false)}
                        </p>
                    )}

                    {/* 5성·4성 갈라 보기 + 보기 전환 — 목록을 거르는 단추라 배치 화면에서는 감춘다 */}
                    {!detailOpen && (
                        <>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 12 }}>
                                {([5, 4] as const).map((g) => (
                                    <button key={g} onClick={() => setGrade(g)} style={pill(grade === g, isMobile)}>
                                        {g}성 방덱
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* 무엇이 잘못됐는지 그대로 띄운다 */}
                {problem && (
                    <div style={{
                        marginBottom: 12,
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(224, 49, 49, 0.35)',
                        color: '#c92a2a',
                        fontSize: isMobile ? 11 : 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8
                    }}>
                        <span>{problem}</span>
                        <button onClick={() => setProblem(null)} style={{ border: 'none', background: 'none', color: '#c92a2a', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                )}

                {loading || !mapping ? (
                    <div style={card()}><Loading /></div>
                ) : (
                    /* 왼쪽 방덱 · 오른쪽 배치. 폰은 위아래로 쌓는다 */
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 'clamp(10px, 2vw, 16px)',
                        alignItems: 'start'
                    }}>
                        {/* ── 왼쪽: 방덱 목록 / 길드원 목록. 폰에서 배치를 볼 때는 접는다 ── */}
                        {!detailOpen && (
                        /*
                          **탭과 목록은 한 칸 안에 있다.** 전투기록 탭이 그렇다 — 탭만 따로 떠 있으면
                          무엇을 가르는 탭인지 붙지 않는다. 그래서 흰 칸 하나에 탭을 머리로 얹고 그
                          아래에 목록을 깐다.
                        */
                        <div style={{ ...card(), padding: 'clamp(10px, 2vw, 12px)', display: 'flex', flexDirection: 'column' }}>

                        {/*
                          **무엇을 세로로 늘어놓을까** — 전투기록 탭의 [길드원|전투기록] 탭과 같은
                          모양이다(같은 자료를 다른 눈으로 보는 자리라 생김새도 같아야 헷갈리지 않는다).
                        */}
                        <div style={{ marginBottom: 'clamp(12px, 2.5vw, 16px)' }}>
                            <div style={{ display: 'flex', width: '100%', borderBottom: '2px solid #eee' }}>
                                {([['deck', '방덱'], ['member', '길드원']] as const).map(([key, label]) => (
                                    <button
                                        key={key}
                                        onClick={() => setViewMode(key)}
                                        data-subtab=""
                                        style={{
                                            flex: 1,
                                            padding: 'clamp(10px, 2vw, 12px) 0',
                                            border: 'none',
                                            background: viewMode === key
                                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                : 'transparent',
                                            color: viewMode === key ? '#fff' : '#666',
                                            fontWeight: 700,
                                            fontSize: isMobile ? 'clamp(12px, 2.5vw, 14px)' : 14,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            borderRadius: 'clamp(10px, 2vw, 12px) clamp(10px, 2vw, 12px) 0 0'
                                        }}
                                    >{label}</button>
                                ))}
                            </div>
                        </div>

                        {viewMode === 'deck' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1.6vw, 10px)' }}>
                            {visibleDecks.length === 0 && (
                                <div style={{ ...card(), textAlign: 'center', padding: 40, color: '#999', fontSize: isMobile ? 12 : 13 }}>
                                    {decks.length ? `${grade}성 방덱이 없습니다.` : '아직 방덱이 없습니다.'}
                                </div>
                            )}
                            {visibleDecks.map((deck) => {
                                /*
                                 * 폰에서는 **직접 고른 덱만** 채운다. `selected` 는 아무것도 안
                                 * 골랐을 때 첫 덱으로 떨어지는데, 목록만 보이는 화면에서 첫 덱이
                                 * 채워져 있으면 "이미 뭔가 열려 있나?" 로 읽힌다.
                                 */
                                const on = (isMobile ? selectedId : selected?.id) === deck.id;
                                return (
                                    <div
                                        key={deck.id}
                                        onClick={() => {
                                            setSelectedId(deck.id);
                                            setFocusMember(null);   // 방덱 목록에서 열면 길드 전체 기록이다
                                            // 폰에서는 화면이 배치로 바뀐다 — 굴려 둔 자리에서 열리면 어디인지 모른다
                                            if (isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        style={{
                                            ...card(),
                                            padding: 'clamp(8px, 1.6vw, 10px)',
                                            cursor: 'pointer',
                                            /**
                                              고른 덱은 **통계 탭에서 방덱을 눌렀을 때와 똑같이** 바탕이
                                              보라로 채워진다(같은 값·같은 그림자). 두 화면에서 같은 것을
                                              고르는 일이니 같아 보여야 한다.
                                            */
                                            background: on
                                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
                                            boxShadow: on
                                                ? '0 8px 24px rgba(102, 126, 234, 0.4)'
                                                : '0 4px 16px rgba(102, 126, 234, 0.1)',
                                            border: on
                                                ? '2px solid rgba(255, 255, 255, 0.3)'
                                                : '1px solid rgba(102, 126, 234, 0.1)',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                        onMouseEnter={(e) => { if (!on) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                        onMouseLeave={(e) => { if (!on) e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        {/* 몹 묶음 — 그림·이름, 그리고 그 아래 방성률 */}
                                        <div style={{ flex: 'none' }}>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                        {deck.keys.map((k, i) => (
                                            <div key={`${deck.id}-${i}`} style={{ textAlign: 'center' }}>
                                                <div style={{
                                                    position: 'relative',
                                                    width: tile,
                                                    height: tile,
                                                    borderRadius: 8,
                                                    border: '2px solid rgba(102, 126, 234, 0.25)',
                                                    background: '#fafafa',
                                                    overflow: 'hidden'
                                                }}>
                                                    <img loading="lazy"
                                                        src={mobImgUrl(cellByKey.get(k)?.imgPath)}
                                                        alt={labelOf(k)}
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    />
                                                    {/* 리더는 방덱의 성격을 정하므로 눈에 보이게 표시한다 */}
                                                    {i === 0 && (
                                                        <span style={{
                                                            position: 'absolute', left: 0, top: 0, padding: '0 3px',
                                                            borderBottomRightRadius: 6, background: 'rgba(102, 126, 234, 0.95)',
                                                            color: '#fff', fontSize: 8, fontWeight: 800, lineHeight: '12px'
                                                        }}>L</span>
                                                    )}
                                                </div>
                                                <div style={{
                                                    marginTop: 3, width: tile,
                                                    fontSize: isMobile ? 9 : 10, fontWeight: 600,
                                                    // 고른 덱은 바탕이 보라라 글씨를 흰색으로 뒤집는다 (통계 탭과 같다)
                                                    color: on ? 'rgba(255, 255, 255, 0.95)' : '#666',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                }}>{labelOf(k)}</div>
                                            </div>
                                        ))}
                                        </div>

                                        {/*
                                          방성률 — **몹 이름 아래**에 숫자와 띠를 나란히 둔다.
                                          숫자만 적으면 덱들을 훑으며 견주기 어렵고, 띠 길이는 눈이 바로
                                          견준다. 몹 묶음 폭에 맞춰 깔면 어느 방덱의 값인지 헷갈리지
                                          않는다. 기록이 없는 조합에는 아무것도 그리지 않는다.
                                        */}
                                        {deck.stats && (
                                            <div
                                                title={`방성률 ${deck.stats.rate}% — ${deck.stats.total}전 중 ${deck.stats.held}번 막음`}
                                                style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}
                                            >
                                                <span style={{
                                                    fontSize: isMobile ? 10 : 11,
                                                    fontWeight: 800,
                                                    color: on ? '#fff' : '#7c3aed',
                                                    whiteSpace: 'nowrap'
                                                }}>{deck.stats.rate}%</span>
                                                <span style={{
                                                    flex: 1,
                                                    height: 5,
                                                    borderRadius: 999,
                                                    overflow: 'hidden',
                                                    background: on ? 'rgba(255, 255, 255, 0.28)' : '#eceefb'
                                                }}>
                                                    <span style={{
                                                        display: 'block',
                                                        width: `${deck.stats.rate}%`,
                                                        height: '100%',
                                                        background: on ? '#fff' : 'linear-gradient(90deg, #7c3aed, #667eea)'
                                                    }} />
                                                </span>
                                                <span style={{
                                                    fontSize: isMobile ? 9 : 10,
                                                    fontWeight: 600,
                                                    color: on ? 'rgba(255, 255, 255, 0.8)' : '#aaa',
                                                    whiteSpace: 'nowrap'
                                                }}>{deck.stats.total}전</span>
                                            </div>
                                        )}
                                        </div>

                                        {/*
                                          누가 맡았는지 **이름을 다 적는다.** 인원수(`4명`)만 적으면 어느
                                          덱을 누가 맡았는지 보려고 덱마다 눌러 봐야 했다. 여럿이면 줄을
                                          넘겨 쌓이고, 카드가 그만큼 길어지는 것은 감수한다.
                                        */}
                                        <div style={{
                                            flex: 1, minWidth: 0,
                                            display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center'
                                        }}>
                                            {seatsOf(deck).map(({ uuid, n }) => {
                                                const m = nameOf.get(uuid);
                                                return (
                                                    <span key={uuid} style={chip(isMobile, on)}>
                                                        <Avatar member={m} size={isMobile ? 14 : 16} />
                                                        {m?.nickname || '알 수 없음'}
                                                        {n > 1 && <Seats n={n} onDark={on} />}
                                                    </span>
                                                );
                                            })}
                                        </div>

                                    </div>
                                );
                            })}

                        </div>
                        )}


                        {/* 길드원별 — 사람이 줄이고, 그 사람이 세운 방덱이 옆에 붙는다 */}
                        {viewMode === 'member' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1.6vw, 10px)' }}>
                            {byMember.placed.length === 0 && (
                                <div style={{ ...card(), textAlign: 'center', padding: 40, color: '#999', fontSize: isMobile ? 12 : 13 }}>
                                    {grade}성 방덱을 세운 사람이 없습니다.
                                </div>
                            )}

                            {byMember.placed.map(({ member: m, decks: mine, record }) => {
                                const isOpen = openMember === m.uuid;
                                return (
                                <div key={m.uuid} style={{
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: 'clamp(12px, 2.5vw, 14px)',
                                    border: isOpen ? '2px solid rgba(102, 126, 234, 0.3)' : '2px solid rgba(102, 126, 234, 0.1)',
                                    boxShadow: isOpen ? '0 6px 20px rgba(102, 126, 234, 0.15)' : '0 3px 10px rgba(102, 126, 234, 0.08)',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease'
                                }}>
                                    {/* 누구인가 — 누르면 접었다 편다 */}
                                    <div
                                        onClick={() => {
                                            // 굴린 자리를 지킨다 — 접으면 화면이 위로 튀어 어디였는지 잃는다
                                            const y = window.scrollY;
                                            setOpenMember(isOpen ? null : m.uuid);
                                            requestAnimationFrame(() => window.scrollTo(0, y));
                                        }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                                            padding: isMobile ? '10px 12px' : '12px 14px'
                                        }}
                                    >
                                        <Avatar member={m} size={isMobile ? 30 : 34} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: isMobile ? 13 : 14, fontWeight: 700, color: '#333',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                            }}>{m.nickname}{m.uuid === myUuid ? ' (나)' : ''}</div>
                                            <div style={{ fontSize: isMobile ? 10 : 11, color: '#888', marginTop: 2 }}>
                                                방덱 {mine.length}종
                                            </div>
                                        </div>
                                        {/*
                                          **그 사람이 세운 덱들의 전적.** 자리 수보다 이게 궁금한 값이다 —
                                          많이 세운 것과 잘 막는 것은 다른 이야기다. 기록이 아직 없는 덱만
                                          세운 사람은 비워 둔다(0% 로 적으면 못 막은 것처럼 읽힌다).
                                        */}
                                        {record ? (
                                            <span
                                                title={`${m.nickname} 님이 방어한 ${record.total}전 중 ${record.held}번 막음 (방성률 ${record.rate}%)`}
                                                style={{
                                                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
                                                    background: 'rgba(102, 126, 234, 0.08)', borderRadius: 10,
                                                    padding: isMobile ? '5px 9px' : '6px 11px'
                                                }}
                                            >
                                                <span style={{ fontSize: isMobile ? 10 : 11, color: '#888', fontWeight: 700 }}>
                                                    {record.total}전
                                                </span>
                                                <span style={{ width: 1, height: isMobile ? 10 : 12, background: 'rgba(0, 0, 0, 0.1)' }} />
                                                <span style={{
                                                    fontSize: isMobile ? 11 : 12, fontWeight: 800,
                                                    color: record.rate >= 20 ? '#1976d2' : '#8b7355'
                                                }}>방성률 {record.rate}%</span>
                                            </span>
                                        ) : (
                                            <span style={{ flexShrink: 0, fontSize: isMobile ? 10 : 11, color: '#bbb', fontWeight: 600 }}>
                                                기록 없음
                                            </span>
                                        )}
                                        <span style={{
                                            flexShrink: 0, fontSize: isMobile ? 12 : 14, color: '#667eea', fontWeight: 700,
                                            transition: 'transform 0.3s ease',
                                            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                                        }}><Caret /></span>
                                    </div>

                                    {/* 무엇을 세웠나 — 누르면 그 덱의 배치로 간다 */}
                                    {isOpen && (
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', gap: 6,
                                        borderTop: '1px solid rgba(102, 126, 234, 0.1)',
                                        background: 'rgba(102, 126, 234, 0.02)',
                                        padding: isMobile ? '10px 12px' : '12px 14px'
                                    }}>
                                        {mine.map(({ deck, n }) => (
                                            <div
                                                key={deck.id}
                                                onClick={() => {
                                                    setSelectedId(deck.id);
                                                    setFocusMember(m.uuid);   // 이 사람 자리의 기록만 본다
                                                    if (isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                    padding: '5px 7px', borderRadius: 10, cursor: 'pointer',
                                                    border: '1px solid rgba(102, 126, 234, 0.12)',
                                                    background: 'rgba(102, 126, 234, 0.04)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {deck.keys.map((k, i) => (
                                                        <div key={`${deck.id}-${i}`} style={{ position: 'relative', width: isMobile ? 30 : 34, height: isMobile ? 30 : 34 }}>
                                                            <img loading="lazy"
                                                                src={mobImgUrl(cellByKey.get(k)?.imgPath)}
                                                                alt={labelOf(k)}
                                                                title={labelOf(k)}
                                                                style={{
                                                                    width: '100%', height: '100%', objectFit: 'contain',
                                                                    borderRadius: 7, background: '#fafafa',
                                                                    border: '1.5px solid rgba(102, 126, 234, 0.2)'
                                                                }}
                                                            />
                                                            {/* 리더는 방덱의 성격을 정하므로 여기서도 짚어 준다 */}
                                                            {i === 0 && (
                                                                <span style={{
                                                                    position: 'absolute', left: 0, top: 0, padding: '0 3px',
                                                                    borderBottomRightRadius: 5, background: 'rgba(102, 126, 234, 0.95)',
                                                                    color: '#fff', fontSize: 7, fontWeight: 800, lineHeight: '10px'
                                                                }}>L</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                {/*
                                                  **이름 대신 전적.** 이름은 바로 옆 그림에 다 있고(그림에
                                                  마우스를 올리면 이름이 뜬다), 여기서 알고 싶은 것은 그 덱이
                                                  **이 사람 자리에서** 얼마나 막아 주느냐다.
                                                */}
                                                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {(() => { const own = deck.memberStats?.[m.uuid]; return own ? (
                                                        <>
                                                            <span style={{
                                                                flexShrink: 0, fontSize: isMobile ? 11 : 12, fontWeight: 800,
                                                                color: own.rate >= 20 ? '#1976d2' : '#8b7355'
                                                            }}>{own.rate}%</span>
                                                            {/* 비율만 있으면 한 판짜리와 삼백 판짜리가 같아 보인다 */}
                                                            <div style={{
                                                                flex: 1, minWidth: 20, height: 5, borderRadius: 3,
                                                                background: 'rgba(102, 126, 234, 0.12)', overflow: 'hidden'
                                                            }}>
                                                                <div style={{
                                                                    width: `${Math.min(100, own.rate)}%`, height: '100%',
                                                                    borderRadius: 3, background: 'linear-gradient(90deg, #667eea, #764ba2)'
                                                                }} />
                                                            </div>
                                                            <span
                                                                title={`${m.nickname} 님 자리: ${own.total}전 중 ${own.held}번 막음`
                                                                    + (deck.stats ? ` · 길드 전체 ${deck.stats.total}전 ${deck.stats.rate}%` : '')}
                                                                style={{ flexShrink: 0, fontSize: isMobile ? 10 : 11, color: '#999', fontWeight: 600 }}
                                                            >{own.total}전</span>
                                                        </>
                                                    ) : (
                                                        <span style={{ fontSize: isMobile ? 10 : 11, color: '#bbb', fontWeight: 600 }}>기록 없음</span>
                                                    ); })()}
                                                </div>
                                                {/* 같은 덱을 여러 자리에 세운 사람이 흔하다 */}
                                                {n > 1 && <Seats n={n} />}
                                            </div>
                                        ))}
                                    </div>
                                    )}
                                </div>
                                );
                            })}

                        </div>
                        )}

                        </div>
                        )}

                        {/* ── 오른쪽: 고른 덱의 배치. 폰에서는 덱을 눌렀을 때만 ── */}
                        {(!isMobile || detailOpen) && (
                        <div style={{
                            ...card(),
                            padding: 'clamp(10px, 2vw, 14px)',
                            /*
                             * 이 칸은 **왼쪽 정렬**이다. 바깥 머리말이 가운데 정렬이라 그게 여기까지
                             * 흘러들어 "배치 7개" 같은 줄만 가운데에 뜨고, 아래 전투기록 줄들과
                             * 시작점이 어긋났다.
                             */
                            textAlign: 'left',
                            // 데스크톱에서는 왼쪽 목록을 굴리는 동안 배치가 붙어 있게 둔다
                            position: isMobile ? undefined : 'sticky',
                            top: isMobile ? undefined : 70
                        }}>
                            {/* 돌아가는 길 — 폰에서만. 어느 덱인지 그림으로 같이 보여 준다 */}
                            {detailOpen && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                                    paddingBottom: 10, borderBottom: '1px solid rgba(102, 126, 234, 0.1)'
                                }}>
                                    <BackButton onClick={() => setSelectedId(null)} size={30} />
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {(selected?.keys || []).map((k, i) => (
                                            <img loading="lazy"
                                                key={`back-${i}`}
                                                src={mobImgUrl(cellByKey.get(k)?.imgPath)}
                                                alt={labelOf(k)}
                                                style={{
                                                    width: 32, height: 32, borderRadius: 6, objectFit: 'contain',
                                                    background: '#fff', border: '1px solid rgba(102, 126, 234, 0.15)'
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!selected ? (
                                <div style={{ textAlign: 'center', padding: 30, color: '#bbb', fontSize: isMobile ? 12 : 13 }}>
                                    방덱을 만들면 여기에 배치가 나옵니다
                                </div>
                            ) : (
                                <>
                                    {/*
                                      어느 덱인지는 **왼쪽에서 테두리로** 알린다 — 이름을 한 줄로 다시
                                      적으면(`암시리 · 불요정왕 · 풍젠이츠`) 같은 말을 두 번 하는 셈이다.
                                    */}
                                    {/* 배치된 사람 */}
                                    {/* 사람 수가 아니라 **세워 둔 개수**다 — 한 사람이 여러 개를 세울 수 있다 */}
                                    <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>
                                        배치 {selected.members.length}개
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
                                        {/* 보여주기만 한다 — 배치는 게임에서 하고 사이트는 받아 적는다 */}
                                        {seatsOf(selected).map(({ uuid, n }) => {
                                            const m = nameOf.get(uuid);
                                            return (
                                                <span key={uuid} style={chip(isMobile)}>
                                                    <Avatar member={m} size={isMobile ? 16 : 18} />
                                                    {m?.nickname || '알 수 없음'}
                                                    {n > 1 && <Seats n={n} />}
                                                </span>
                                            );
                                        })}
                                    </div>

                                    <div style={{ height: 1, background: 'rgba(102, 126, 234, 0.15)', margin: '10px 0' }} />

                                    {/*
                                      **배치 가능한 인원** — 손으로 배치하는 자리가 아니라 보여주는 자리다.
                                      배치는 게임에서 하고, 사이트는 그 현황을 받아 적는다(플러그인이 올린다).
                                      여기서는 보유몹으로 봤을 때 **이 방덱을 세울 수 있는 사람만** 적는다 —
                                      못 세우는 사람까지 늘어놓으면 길드원 전체가 회색으로 깔려 읽을 수가 없다.
                                    */}
                                    {(() => {
                                        const able = members.filter((m) => fieldable(m.uuid, selected.keys).ok);
                                        return (
                                            <>
                                                <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>
                                                    배치 가능한 인원 {able.length}명
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                    {able.map((m) => (
                                                        <span key={m.uuid} style={chip(isMobile)}>
                                                            <Avatar member={m} size={isMobile ? 16 : 18} />
                                                            {m.nickname}{m.uuid === myUuid ? ' (나)' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()}

                                    <div style={{ height: 1, background: 'rgba(102, 126, 234, 0.15)', margin: '12px 0 10px' }} />

                                    {/*
                                      **이 덱으로 방어한 기록.** 승패는 방어한 길드원 기준이다 — 막아냈으면 승.
                                      기록이 수백 건이라 칸 안에서만 굴린다(화면 전체가 길어지면 배치가 밀려난다).
                                    */}
                                    <div style={{
                                        display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                                        fontSize: isMobile ? 11 : 12, fontWeight: 800, color: '#667eea', marginBottom: 6
                                    }}>
                                        <span>전투기록{recordStat ? ` ${recordStat.total}전` : ''}</span>
                                        {/* 누구 기록만 보고 있는지 — 누르면 길드 전체로 돌아간다 */}
                                        {focusMember && (
                                            <span
                                                onClick={() => setFocusMember(null)}
                                                title="눌러서 길드 전체 기록 보기"
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                                                    fontSize: isMobile ? 10 : 11, fontWeight: 700, color: '#667eea',
                                                    background: 'rgba(102, 126, 234, 0.10)', borderRadius: 999,
                                                    padding: isMobile ? '3px 8px' : '4px 10px'
                                                }}
                                            >
                                                <Avatar member={nameOf.get(focusMember)} size={isMobile ? 14 : 16} />
                                                {nameOf.get(focusMember)?.nickname || '이 사람'} 기록만
                                                <span style={{ color: '#aaa', fontWeight: 800 }}>×</span>
                                            </span>
                                        )}
                                        {recordStat && (
                                            <span style={{ fontWeight: 700, color: '#888' }}>
                                                방성률 {recordStat.rate}%
                                                <span style={{ color: '#64B5F6', marginLeft: 6 }}>{recordStat.win}성공</span>
                                                <span style={{ color: '#e57373', marginLeft: 4 }}>{recordStat.total - recordStat.win}실패</span>
                                            </span>
                                        )}
                                    </div>

                                    {!recordsLoading && <DefenseTrend records={shownRecords} compact={isMobile} />}

                                    {recordsLoading ? (
                                        <Loading padding={18} size={22} />
                                    ) : !shownRecords || shownRecords.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: 16, color: '#bbb', fontSize: isMobile ? 11 : 12 }}>
                                            {focusMember
                                                ? `${nameOf.get(focusMember)?.nickname || '이 사람'} 님이 이 방덱으로 싸운 기록이 없습니다`
                                                : '아직 이 방덱으로 싸운 기록이 없습니다'}
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', gap: 4,
                                            maxHeight: isMobile ? 260 : 320, overflowY: 'auto'
                                        }}>
                                            {shownRecords.map((r: any, i: number) => (
                                                <div
                                                    key={r._id || i}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        padding: isMobile ? '5px 7px' : '6px 9px',
                                                        borderRadius: 8,
                                                        background: i % 2 === 0 ? 'rgba(248, 250, 255, 0.9)' : 'transparent',
                                                        border: '1px solid rgba(102, 126, 234, 0.1)'
                                                    }}
                                                >
                                                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                                        <ProfileWithName
                                                            profileImg={r.profileImg}
                                                            nickname={r.author}
                                                            size={isMobile ? 18 : 20}
                                                            fontSize={isMobile ? 11 : 12}
                                                            borderRadius={6}
                                                            color={r.isDeleted ? '#ff6b6b' : '#333'}
                                                        />
                                                    </div>
                                                    {/* 승패는 방어한 길드원 기준 — 막아냈으면 승이다 */}
                                                    <span style={{
                                                        flexShrink: 0, width: isMobile ? 46 : 52, textAlign: 'center',
                                                        fontSize: isMobile ? 10 : 11, fontWeight: 800, whiteSpace: 'nowrap',
                                                        color: r.result === 'win' ? '#64B5F6' : '#e57373'
                                                    }}>{r.result === 'win' ? '방어 성공' : '방어 실패'}</span>
                                                    {/*
                                                      상대 길드 — 엠블럼과 이름. 엠블럼은 사람이 게임 화면에서
                                                      잘라 올린 것이라 없는 길드가 많다. 없으면 물음표 그림으로
                                                      떨어뜨린다(다른 화면과 같은 길이다).
                                                    */}
                                                    <span style={{
                                                        // 길이가 들쭉날쭉해도 칸은 고정이다 — 이름이 길어질 때마다
                                                        // 앞의 성공/실패가 좌우로 흔들리면 눈이 줄을 못 따라간다
                                                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
                                                        width: isMobile ? 82 : 104, fontSize: isMobile ? 10 : 11, color: '#888',
                                                        overflow: 'hidden', whiteSpace: 'nowrap'
                                                    }}>
                                                        {r.oppGuildName && (
                                                            <img loading="lazy"
                                                                src={guildImg(r.oppGuildName)}
                                                                alt=""
                                                                style={{
                                                                    width: isMobile ? 14 : 16, height: isMobile ? 14 : 16,
                                                                    borderRadius: 4, objectFit: 'cover', flexShrink: 0,
                                                                    border: '1px solid rgba(0, 0, 0, 0.08)'
                                                                }}
                                                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/images/unknown2.png'; }}
                                                            />
                                                        )}
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.oppGuildName}</span>
                                                    </span>
                                                    <span style={{ flexShrink: 0, fontSize: isMobile ? 9 : 10, color: '#aaa' }}>{r.date}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        )}
                    </div>
                )}
            </div>


        </div>
    );
};


/**
 * 몇 자리를 세웠나.
 *
 * `×2` 라고 적었더니 곱셈 기호가 이름 옆에서 지저분했다. 숫자만 든 작은 알을 붙인다 —
 * 보유몹 격자에서 마리 수를 적는 방식과 같아, 한 번 익힌 눈으로 그대로 읽힌다.
 */
const Seats: React.FC<{ n: number; onDark?: boolean }> = ({ n, onDark }) => (
    <span
        title={`${n}개`}
        style={{
            minWidth: 15,
            height: 15,
            padding: '0 4px',
            borderRadius: 999,
            background: onDark ? 'rgba(255, 255, 255, 0.9)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: onDark ? '#4c56b5' : '#fff',
            fontSize: 9,
            fontWeight: 800,
            lineHeight: '15px',
            textAlign: 'center'
        }}
    >{n}</span>
);

/**
 * 프로필 그림.
 *
 * 얼굴을 안 올린 사람이 있다(길드원 스물아홉 중 넷). 그때는 **길드원 탭과 같은 기본
 * 이미지**를 쓴다 — 닉네임 첫 글자를 그리면 같은 사람이 화면마다 달라 보인다.
 * 파일이 없어 그림이 깨질 때도 같은 기본 이미지로 되돌린다.
 */
const DEFAULT_PROFILE = '/images/default-profile.png';

const Avatar: React.FC<{ member?: Member; size: number }> = ({ member, size }) => (
    <img loading="lazy"
        src={member?.profileImg || DEFAULT_PROFILE}
        alt={member?.nickname || '프로필'}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', background: '#fff', flex: 'none' }}
        onError={(e) => {
            const img = e.target as HTMLImageElement;
            if (!img.src.endsWith(DEFAULT_PROFILE)) img.src = DEFAULT_PROFILE;
        }}
    />
);

function card(): React.CSSProperties {
    return {
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 'clamp(12px, 2.5vw, 16px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        padding: 'clamp(12px, 2.5vw, 16px)'
    };
}

/**
 * 배치된 사람 이름표.
 *
 * `onDark` 는 고른 덱(보라 바탕) 위에 놓일 때다 — 그때는 흰 반투명 바탕에 흰 글씨로
 * 뒤집어야 읽힌다.
 */
function chip(isMobile: boolean, onDark = false): React.CSSProperties {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px 3px 3px',
        borderRadius: 999,
        background: onDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(102, 126, 234, 0.12)',
        color: onDark ? '#fff' : '#4c56b5',
        fontSize: isMobile ? 10 : 11,
        fontWeight: 700,
        maxWidth: '100%'
    };
}

/**
 * 갈라 보기 알약 (5성 방덱 / 4성 방덱).
 *
 * 보유몹 화면의 성급 알약과 같은 모양이다 — 같은 일을 하는 단추가 화면마다 다르면 안 된다.
 * 흰 카드 위에 놓이므로 꺼진 알약도 흰 바탕이다.
 */
function pill(active: boolean, isMobile: boolean): React.CSSProperties {
    return {
        padding: isMobile ? '5px 12px' : '6px 14px',
        borderRadius: 999,
        border: active ? '1px solid #667eea' : '1px solid rgba(102, 126, 234, 0.25)',
        background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
        color: active ? '#fff' : '#667eea',
        fontSize: isMobile ? 11 : 12,
        fontWeight: 800,
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    };
}

export default DeckPlan;
